/* Created by Interactive 63 - https://interactive63.com */
/**
 * CSS that only exports need: the CSS-only hover trigger, the reduced motion rules, and the
 * responsive sizing rule. Each is a separate function so it can be tested alone.
 */
import type { Project, Track } from '../model/types';
import { sampleTrack } from '../timeline/sample';
import { elementGroups, isAnimatedTrack, type CssScene } from './css';
import { cssDeclarations } from './values';
import type { AnimatedElement } from './types';

export type ReducedMotionMode = 'final-frame' | 'first-frame' | 'ignore';

/** Joins selectors into one comma-separated list, one per line. */
function selectorList(selectors: readonly string[]): string {
  return selectors.join(',\n');
}

/**
 * Pauses every animation until the pointer is over the scene, then lets it run. Pausing and
 * resuming never restarts an animation, so tracks that already finished stay finished.
 */
export function hoverPauseCss(animatedSelectors: readonly string[], sceneClass: string): string {
  if (animatedSelectors.length === 0) return '';
  const hovering = animatedSelectors.map((s) =>
    s.replace(`.${sceneClass} `, `.${sceneClass}:hover `),
  );
  return [
    `${selectorList(animatedSelectors)} {\n  animation-play-state: paused;\n}`,
    `${selectorList(hovering)} {\n  animation-play-state: running;\n}`,
  ].join('\n\n');
}

/** Holds every animation at its start until a script starts it. */
export function pausedCss(animatedSelectors: readonly string[]): string {
  if (animatedSelectors.length === 0) return '';
  return `${selectorList(animatedSelectors)} {\n  animation-play-state: paused;\n}`;
}

/** Makes a click-triggered scene look and behave like a button. */
export function clickCss(sceneClass: string): string {
  return [
    `.${sceneClass} {\n  cursor: pointer;\n}`,
    `.${sceneClass}:focus-visible {\n  outline: 2px solid currentColor;\n  outline-offset: 4px;\n}`,
  ].join('\n\n');
}

/**
 * Static poses for visitors who prefer reduced motion. Each animated element gets a rule that
 * stops its animation and holds the pose at the end (or start) of the timeline. With `forced`
 * the rules are written without the media query, to simulate the setting.
 */
export function reducedMotionCss(
  project: Project,
  animated: readonly AnimatedElement[],
  scene: CssScene,
  mode: ReducedMotionMode,
  duration: number,
  forced = false,
  extraRules: readonly string[] = [],
): string {
  if (mode === 'ignore') return '';
  const time = mode === 'final-frame' ? duration : 0;
  const rules: string[] = [];
  for (const element of animated) {
    for (const group of elementGroups(element, scene)) {
      if (!group.tracks.some(isAnimatedTrack)) continue;
      const declarations = ['animation: none !important'];
      for (const track of group.tracks) {
        declarations.push(...staticDeclarations(track, time, duration, element.meta.pathLength));
      }
      const body = declarations.map((d) => `  ${d};`).join('\n');
      rules.push(`${group.selector} {\n${body}\n}`);
    }
  }
  rules.push(...extraRules);
  if (rules.length === 0) return '';
  if (forced) return rules.join('\n\n');
  const inner = rules
    .join('\n\n')
    .split('\n')
    .map((line) => (line === '' ? line : `  ${line}`))
    .join('\n');
  return `@media (prefers-reduced-motion: reduce) {\n${inner}\n}`;
}

/** The declarations for a track's value at a time. */
function staticDeclarations(
  track: Track,
  time: number,
  duration: number,
  pathLength: number | undefined,
): string[] {
  const value = sampleTrack(track, time, duration);
  return value === undefined ? [] : cssDeclarations(track.property, value, { pathLength });
}

/** Makes the exported SVG fill its container's width and keep its aspect ratio. */
export function responsiveCss(sceneClass: string): string {
  return `.${sceneClass} .st-svg {\n  display: block;\n  width: 100%;\n  height: auto;\n}`;
}
